import React, { useMemo, useState } from 'react';
import { Box, Checkbox, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import numeral from 'numeral';
import { fNumber } from 'src/utils/formatNumber';
import type { QITAnswer } from 'src/api/dataVault/sectionAnswers';

interface Props {
  qit: { id: number; label: string };
  answer: QITAnswer | null;
  selectionMode: boolean;
  isSelected: boolean;
  onToggleSelect: (id: number | string) => void;
  hideBorder?: boolean;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '';
  }
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format a date string (YYYY-MM-DD or ISO timestamp) as MM/DD/YYYY.
 * Handles both date-only strings and full UTC timestamps sent by the backend.
 * Parses to a local date to avoid UTC midnight off-by-one in negative-offset timezones.
 */
function formatDateAnswer(dateStr: string): string {
  if (!dateStr) {
    return dateStr;
  }
  // Extract date portion from ISO timestamps like '2024-04-04T06:00:00.000Z'
  const datePart = dateStr.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length !== 3) {
    return dateStr;
  }
  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) {
    return dateStr;
  }
  return `${String(month).padStart(2, '0')}/${String(day).padStart(
    2,
    '0'
  )}/${year}`;
}

/**
 * Mask an SSN string. Preserves dashes if present (e.g. "123-45-6789" → "•••-••-6789").
 * Falls back to a fixed mask if the format is unrecognised.
 */
function maskSsn(value: string): string {
  if (!value) {
    return value;
  }
  // Standard SSN with dashes: ###-##-####
  if (/^\d{3}-\d{2}-\d{4}$/.test(value)) {
    return `•••-••-${value.slice(-4)}`;
  }
  // 9-digit SSN without dashes
  if (/^\d{9}$/.test(value)) {
    return `•••••${value.slice(-4)}`;
  }
  // Unknown format — mask all but last 4 characters
  const visible = value.slice(-4);
  return `•••-••-${visible}`;
}

/**
 * Determine the display status for a file-type answer.
 */
function getFileStatus(answer: QITAnswer): string {
  const fileCount = answer.files?.length ?? 0;
  if (fileCount === 0) {
    return 'Not answered';
  }
  const required = answer.numberOfFilesRequired || 1;
  return fileCount >= required ? 'Complete' : 'Incomplete';
}

export const QITAnswerRow = React.memo(function QITAnswerRow({
  qit,
  answer,
  selectionMode,
  isSelected,
  onToggleSelect,
  hideBorder,
}: Props): React.ReactElement {
  const theme = useTheme();
  const [ssnVisible, setSsnVisible] = useState(false);
  const isSsn = answer?.questionType === 'ssn';

  const rawDisplayValue = useMemo(() => {
    if (answer?.questionType === 'file') {
      return getFileStatus(answer);
    }
    return answer?.displayValue || 'Not answered';
  }, [answer]);
  const displayValue = (() => {
    if (!answer?.displayValue) {
      return rawDisplayValue;
    }
    if (answer.questionType === 'date') {
      return formatDateAnswer(answer.displayValue);
    }
    if (isSsn) {
      return ssnVisible ? answer.displayValue : maskSsn(answer.displayValue);
    }
    if (answer.questionType === 'currency') {
      const parsed = parseFloat(answer.displayValue.replace(/[^0-9.-]/g, ''));
      return isNaN(parsed)
        ? answer.displayValue
        : numeral(parsed).format('$0,0.00');
    }
    if (
      answer.questionType === 'number' ||
      answer.questionType === 'calculation'
    ) {
      const parsed = parseFloat(answer.displayValue.replace(/[^0-9.-]/g, ''));
      return isNaN(parsed) ? answer.displayValue : fNumber(parsed);
    }
    return rawDisplayValue;
  })();
  const fileStatusColor =
    rawDisplayValue === 'Not answered' || rawDisplayValue === 'Incomplete'
      ? theme.palette.mode === 'dark'
        ? theme.palette.info.light
        : theme.palette.info.dark
      : 'text.primary';

  const lastUpdated = answer?.lastUpdatedAt
    ? formatDate(answer.lastUpdatedAt)
    : null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        py: 1.5,
        px: 2,
        borderBottom: hideBorder
          ? 'none'
          : `1px solid ${theme.palette.divider}`,
        cursor: selectionMode ? 'pointer' : 'default',
        '&:hover': selectionMode ? { bgcolor: 'action.hover' } : {},
      }}
      onClick={selectionMode ? () => onToggleSelect(qit.id) : undefined}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 12,
            mb: 0.25,
          }}
        >
          {qit.label}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography
            sx={{
              color: fileStatusColor,
              fontSize: 15,
              fontWeight: 600,
              fontFamily: 'Inter, sans-serif',
              letterSpacing: isSsn && !ssnVisible ? '0.05em' : undefined,
            }}
          >
            {displayValue}
          </Typography>
          {isSsn && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSsnVisible((v) => !v);
              }}
              aria-label={ssnVisible ? 'Hide SSN' : 'Show SSN'}
              sx={{ color: 'text.secondary', p: 0.25 }}
            >
              {ssnVisible ? (
                <VisibilityOffIcon sx={{ fontSize: 16 }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          )}
        </Stack>
        {lastUpdated && (
          <Typography
            sx={{
              color: 'text.disabled',
              fontSize: 11,
              mt: 0.25,
            }}
          >
            Last updated on: {lastUpdated}
          </Typography>
        )}
        {answer?.isStale && (
          <Typography
            sx={{
              color: 'warning.dark',
              fontSize: 11,
              fontWeight: 500,
              mt: 0.25,
            }}
          >
            Needs update
          </Typography>
        )}
      </Box>

      {selectionMode && (
        <Checkbox
          checked={isSelected}
          onChange={() => onToggleSelect(qit.id)}
          onClick={(e) => e.stopPropagation()}
          inputProps={{ 'aria-label': `Re-answer ${qit.label}` }}
          sx={{
            ml: 1,
            color: 'text.disabled',
            '&.Mui-checked': {
              color: 'warning.main',
            },
          }}
        />
      )}
    </Stack>
  );
});
